/**
 * ANAHTAR KELİME ARAŞTIRMA — Semrush Keyword Magic Tool paritesi.
 * Üst: seed + pazar + Araştır (maliyet onaylı, async koşu, canlı durum).
 * Genel bakış: Hacim (+global), KD göstergesi, Amaç, CPC/Rekabet, 12 ay trend, SERP özellikleri.
 * Fikirler: sol grup paneli (kelime kökleri) · sekmeler (Tümü/Sorular/İlgili) · arama · sıralama ·
 * satırlarda intent rozeti, trend sparkline, renkli KD · seçilenleri Sıralama Takibine ekleme.
 */
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  KeyRound, Search, TrendingUp, CircleAlert, Trash2, Target, ChevronDown,
} from "lucide-react";
import { useListDomains } from "@workspace/api-client-react";
import { useActiveDomain } from "@/hooks/use-active-domain";
import { confirmDialog } from "@/components/confirm-dialog";

type Idea = {
  keyword: string; volume: number; difficulty: number | null; cpc: number;
  competition: number | null; intent: string | null; monthly: number[];
  serpFeatures: string[]; kind: "iceren" | "ilgili"; question: boolean;
};
type GapRow = {
  keyword: string; volume: number; difficulty: number | null; intent: string | null;
  positions: (number | null)[]; category: "eksik" | "zayif" | "guclu" | "ortak";
};
type SerpVeri = { organic: { domain: string; url: string; title: string; position: number }[]; paa: string[]; aiOverview: { answer: string; sources: { domain: string; url: string }[] } | null };
type Research = {
  id: number; seed: string; market: string; status: string; error: string | null; createdAt: string;
  data: {
    gap?: { domains: string[]; rows: GapRow[] };
    serp?: SerpVeri;
    overview: { keyword: string; volume: number; globalVolume: number; difficulty: number | null; intent: string | null; cpc: number; competition: number | null; monthly: number[]; serpFeatures: string[] } | null;
    ideas: Idea[];
  } | null;
};

const GAP_CATS: { key: GapRow["category"] | "hepsi"; label: string; hint: string }[] = [
  { key: "eksik", label: "Eksik", hint: "Rakip sıralanıyor, siz yoksunuz — en değerli fırsatlar" },
  { key: "zayif", label: "Zayıf", hint: "Siz varsınız ama rakip önde" },
  { key: "guclu", label: "Güçlü", hint: "Rakipten öndesiniz" },
  { key: "ortak", label: "Sadece Sizde", hint: "Rakip bu kelimede hiç yok" },
  { key: "hepsi", label: "Tümü", hint: "" },
];

function PosBadge({ pos }: { pos: number | null }) {
  if (pos === null) return <span className="text-gray-300 dark:text-gray-600">—</span>;
  const cls = pos <= 3 ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
    : pos <= 10 ? "bg-lime-100 text-lime-700 dark:bg-lime-500/15 dark:text-lime-300"
      : pos <= 20 ? "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300"
        : "bg-gray-100 text-gray-500 dark:bg-white/10 dark:text-gray-400";
  return <span className={`inline-block min-w-[34px] rounded-md px-1.5 py-0.5 text-center text-xs font-semibold tabular-nums ${cls}`}>{pos}</span>;
}
type ListRow = { id: number; seed: string; market: string; status: string; error: string | null; createdAt: string; ideaCount: number };

const API = "/api";
async function jfetch(path: string, init?: RequestInit): Promise<unknown> {
  const res = await fetch(`${API}${path}`, { ...init, headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) } });
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) throw new Error(body.error ?? `HTTP ${res.status}`);
  return body;
}

const MARKETS: { key: string; label: string }[] = [
  { key: "tr", label: "Türkçe (TR)" }, { key: "en", label: "İngilizce (US)" }, { key: "en-gb", label: "İngilizce (UK)" },
  { key: "de", label: "Almanca (DE)" }, { key: "ar", label: "Arapça (SA)" }, { key: "fr", label: "Fransızca (FR)" },
  { key: "es", label: "İspanyolca (ES)" }, { key: "ru", label: "Rusça (RU)" }, { key: "nl", label: "Felemenkçe (NL)" },
];

const INTENT_TR: Record<string, { label: string; letter: string; cls: string }> = {
  informational: { label: "Bilgi", letter: "B", cls: "bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300" },
  commercial: { label: "Ticari", letter: "T", cls: "bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300" },
  transactional: { label: "İşlem", letter: "İ", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300" },
  navigational: { label: "Gezinme", letter: "G", cls: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300" },
};

const SERP_TR: Record<string, string> = {
  organic: "Organik", paid: "Reklam", featured_snippet: "Öne çıkan snippet", people_also_ask: "Sorular (PAA)",
  images: "Görseller", video: "Video", local_pack: "Yerel paket", knowledge_graph: "Bilgi paneli",
  related_searches: "İlgili aramalar", shopping: "Alışveriş", top_stories: "Haberler", ai_overview: "AI Overview",
  people_also_search: "Bunu arayanlar", carousel: "Karusel", map: "Harita", reviews: "Yorumlar",
};

function kdMeta(kd: number | null): { label: string; color: string; bar: string } {
  if (kd === null) return { label: "—", color: "text-gray-400", bar: "#9ca3af" };
  if (kd < 30) return { label: "Kolay", color: "text-emerald-600", bar: "#10b981" };
  if (kd < 50) return { label: "Orta", color: "text-lime-600", bar: "#84cc16" };
  if (kd < 70) return { label: "Zor", color: "text-amber-600", bar: "#f59e0b" };
  return { label: "Çok zor", color: "text-red-600", bar: "#ef4444" };
}

const fmtNum = (n: number): string => n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(n >= 10_000 ? 0 : 1)}K` : String(n);

function Sparkline({ data, width = 72, height = 22 }: { data: number[]; width?: number; height?: number }) {
  if (!data.length) return <span className="text-xs text-gray-300">—</span>;
  const max = Math.max(...data, 1);
  const pts = data.map((v, i) => `${(i / Math.max(data.length - 1, 1)) * width},${height - (v / max) * (height - 2) - 1}`).join(" ");
  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline points={pts} fill="none" stroke="#e83e3c" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

// Türkçe + İngilizce dolgu kelimeleri — grup panelinde kök çıkarırken atlanır
const STOP = new Set(["ve", "ile", "için", "icin", "en", "bir", "mi", "mı", "mu", "mü", "ne", "nasıl", "nasil", "the", "for", "and", "in", "of", "to", "a", "is", "how", "what"]);

export default function KeywordResearchPage() {
  const { data: domains } = useListDomains();
  const [activeDomainId] = useActiveDomain(domains);
  const qc = useQueryClient();

  const [seed, setSeed] = useState("");
  const [inputMode, setInputMode] = useState<"single" | "bulk" | "gap">("single");
  const [bulkText, setBulkText] = useState("");
  const [compText, setCompText] = useState("");
  const [gapCat, setGapCat] = useState<GapRow["category"] | "hepsi">("eksik");
  const [market, setMarket] = useState("tr");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [tab, setTab] = useState<"tumu" | "soru" | "ilgili">("tumu");
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState<"volume" | "difficulty" | "cpc">("volume");
  const [group, setGroup] = useState<string | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [flash, setFlash] = useState<string | null>(null);

  const { data: list } = useQuery<{ researches: ListRow[] }>({
    queryKey: ["kw-research-list", activeDomainId],
    queryFn: () => jfetch(`/domains/${activeDomainId}/keyword-research`) as Promise<{ researches: ListRow[] }>,
    enabled: !!activeDomainId,
    refetchInterval: (query) => (query.state.data?.researches.some((r) => r.status === "running") ? 3000 : false),
  });

  const activeId = selectedId ?? list?.researches.find((r) => r.status === "done")?.id ?? list?.researches[0]?.id ?? null;
  const running = list?.researches.some((r) => r.status === "running") ?? false;

  const { data: research } = useQuery<Research>({
    queryKey: ["kw-research", activeId],
    queryFn: () => jfetch(`/keyword-research/${activeId}`) as Promise<Research>,
    enabled: !!activeId,
    refetchInterval: (query) => (query.state.data?.status === "running" ? 3000 : false),
  });

  const say = (m: string) => { setFlash(m); window.setTimeout(() => setFlash(null), 5000); };

  const bulkCount = useMemo(() =>
    new Set(bulkText.split(/[\n,]+/).map((k) => k.trim().toLocaleLowerCase("tr")).filter((k) => k.length >= 2)).size,
  [bulkText]);

  const compCount = useMemo(() =>
    new Set(compText.split(/[\n,]+/).map((c) => c.trim().toLowerCase()).filter((c) => c.includes("."))).size,
  [compText]);

  const startResearch = async (): Promise<void> => {
    if (!activeDomainId) return;
    const isBulk = inputMode === "bulk";
    const isGap = inputMode === "gap";
    const s = seed.trim();
    if (isGap ? compCount < 1 : isBulk ? bulkCount < 2 : !s) return;
    const ok = await confirmDialog({
      title: isGap ? `${Math.min(compCount, 3)} rakiple Keyword Gap analizi` : isBulk ? `${Math.min(bulkCount, 100)} kelime için toplu analiz` : `"${s}" için kelime araştırması`,
      description: `Seçili pazar: ${MARKETS.find((m) => m.key === market)?.label}. ${isGap
        ? "Sizin ve rakiplerin sıralandığı tüm kelimeler karşılaştırılıp Eksik/Zayıf/Güçlü fırsat kategorilerine ayrılacak."
        : isBulk
          ? "Her kelimenin hacim, zorluk, amaç, CPC ve 12 aylık trendi tek koşuda çekilecek."
          : "Hacim, zorluk, amaç, trend ve yüzlerce kelime fikri çekilecek."}`,
      lines: [isGap ? "Tahmini maliyet: ~60 token/rakip (3 rakip ≈ 200)" : isBulk ? "Tahmini maliyet: ~30 token" : "Tahmini maliyet: ~80 token", "Sonuç kaydedilir — tekrar görüntüleme ücretsizdir"],
      confirmText: "Araştır",
    });
    if (!ok) return;
    try {
      const body = isGap ? { competitors: compText, market } : isBulk ? { keywords: bulkText, market } : { seed: s, market };
      const out = (await jfetch(`/domains/${activeDomainId}/keyword-research`, { method: "POST", body: JSON.stringify(body) })) as { id: number };
      setSelectedId(out.id);
      setSeed(""); setBulkText(""); setCompText("");
      setGroup(null); setChecked(new Set()); setTab("tumu"); setQ(""); setGapCat("eksik");
      void qc.invalidateQueries({ queryKey: ["kw-research-list", activeDomainId] });
    } catch (e) { say((e as Error).message); }
  };

  const removeResearch = async (row: ListRow): Promise<void> => {
    const ok = await confirmDialog({ title: "Araştırmayı sil", description: `"${row.seed}" araştırması silinsin mi?`, confirmText: "Sil", danger: true });
    if (!ok) return;
    await jfetch(`/keyword-research/${row.id}`, { method: "DELETE" });
    if (selectedId === row.id) setSelectedId(null);
    void qc.invalidateQueries({ queryKey: ["kw-research-list", activeDomainId] });
  };

  const addToTracking = async (): Promise<void> => {
    if (!checked.size || !activeDomainId) return;
    const kws = [...checked];
    const ok = await confirmDialog({
      title: `${kws.length} kelimeyi Sıralama Takibine ekle`,
      description: "Hacim ve zorluk verileri otomatik çekilir; pozisyonlar her gece ölçülür.",
      lines: kws.slice(0, 6).map((k) => `• ${k}`).concat(kws.length > 6 ? [`… +${kws.length - 6} kelime`] : []),
      confirmText: "Takibe Ekle",
    });
    if (!ok) return;
    try {
      const j = (await jfetch(`/domains/${activeDomainId}/rank-tracking/keywords`, {
        method: "POST",
        body: JSON.stringify({ keywords: kws.join("\n"), market: research?.market ?? "tr" }),
      })) as { added: number; skipped: number };
      say(`${j.added} kelime takibe eklendi${j.skipped ? ` (${j.skipped} zaten vardı)` : ""} — bu gece otomatik ölçülecek.`);
      setChecked(new Set());
    } catch (e) { say((e as Error).message); }
  };

  const ideas = research?.data?.ideas ?? [];
  const ov = research?.data?.overview ?? null;

  // Sol grup paneli: fikirlerdeki en sık kelime kökleri (seed kelimeleri hariç)
  const groups = useMemo(() => {
    const seedWords = new Set((research?.seed ?? "").toLocaleLowerCase("tr").split(/\s+/));
    const freq = new Map<string, number>();
    for (const i of ideas) {
      for (const w of i.keyword.toLocaleLowerCase("tr").split(/\s+/)) {
        if (w.length < 3 || seedWords.has(w) || STOP.has(w)) continue;
        freq.set(w, (freq.get(w) ?? 0) + 1);
      }
    }
    return [...freq.entries()].filter(([, n]) => n >= 3).sort((a, b) => b[1] - a[1]).slice(0, 14);
  }, [ideas, research?.seed]);

  const filtered = useMemo(() => {
    let rows = ideas;
    if (tab === "soru") rows = rows.filter((i) => i.question);
    if (tab === "ilgili") rows = rows.filter((i) => i.kind === "ilgili");
    if (group) rows = rows.filter((i) => i.keyword.toLocaleLowerCase("tr").includes(group));
    if (q.trim()) { const needle = q.toLocaleLowerCase("tr"); rows = rows.filter((i) => i.keyword.includes(needle)); }
    return [...rows].sort((a, b) => sortBy === "volume" ? b.volume - a.volume : sortBy === "cpc" ? b.cpc - a.cpc : (b.difficulty ?? -1) - (a.difficulty ?? -1));
  }, [ideas, tab, group, q, sortBy]);

  const toggle = (kw: string) => setChecked((prev) => { const n = new Set(prev); if (n.has(kw)) n.delete(kw); else n.add(kw); return n; });

  type TrendK = { keyword: string; volume: number; buyume: number; sonOrt: number; monthly: number[]; seed: string };
  const { data: trend } = useQuery<{ kosuSayisi: number; yukselenler: TrendK[]; sonenler: TrendK[]; elenen?: number }>({
    queryKey: ["trend-kelimeler", activeDomainId],
    enabled: !!activeDomainId,
    staleTime: 10 * 60_000,
    queryFn: async () => (await fetch(`/api/domains/${activeDomainId}/trend-kelimeler`)).json(),
  });

  const kd = kdMeta(ov?.difficulty ?? null);
  const intent = ov?.intent ? INTENT_TR[ov.intent] : null;

  if (!activeDomainId) return <div className="p-8 text-sm text-gray-500">Önce bir site seçin.</div>;

  return (
    <div className="space-y-5 p-4 md:p-6">
      {/* TREND RADARI (Faz 4.2): koşularda saklanan 12 aylık serilerden — sıfır ek maliyet.
          Kart HER ZAMAN görünür (kullanıcı "bulamadım" dedi) — boş halleri açıklayıcı. */}
      {trend && (
        <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-white/[0.02]">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold"><TrendingUp className="h-4 w-4 text-emerald-500" /> Trend Kelime Radarı <span className="text-[11px] font-normal text-gray-400">— son 3 ayda %25+ büyüyen sorgular (araştırma koşularının 12 aylık serilerinden); rekabet bastırmadan yakala</span></div>
          {trend.kosuSayisi === 0 ? (
            <p className="text-[12.5px] text-gray-400">Radar, yaptığın kelime araştırmalarının 12 aylık hacim serilerinden beslenir — henüz tamamlanmış koşu yok. Aşağıdan bir kelime araştır; her koşu radarın havuzunu büyütür.</p>
          ) : trend.yukselenler.length === 0 ? (
            <p className="text-[12.5px] text-gray-400">{trend.kosuSayisi} koşu tarandı — şu an %25+ yükselen kelime yok. Yeni araştırmalar yaptıkça (özellikle sektörel geniş sorgularla) radar dolacak.</p>
          ) : null}
          <div className="flex flex-wrap gap-2">
            {trend.yukselenler.slice(0, 12).map((t) => (
              <span key={t.keyword} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[12px] dark:border-emerald-900 dark:bg-emerald-950/40"
                title={`aylık ~${t.sonOrt.toLocaleString("tr-TR")} arama · kaynak koşu: "${t.seed}" · son 12 ay: ${t.monthly.join(" → ")}`}>
                {t.keyword} <b className="text-emerald-600 dark:text-emerald-400">+%{t.buyume}</b>
              </span>
            ))}
          </div>
          {(trend.elenen ?? 0) > 0 && (
            <div className="mt-2 text-[11px] text-gray-400">{trend.elenen} kelime bu siteyle ilgisiz görünüp gizlendi (sayfa hedef kelimeleri ve cluster'larla örtüşmüyor).</div>
          )}
          {trend.sonenler.length > 0 && (
            <div className="mt-2 text-[11.5px] text-gray-400">Sönenler: {trend.sonenler.slice(0, 5).map((t) => `${t.keyword} (%${t.buyume})`).join(" · ")} — bunlara yeni içerik yatırımı yapma.</div>
          )}
        </div>
      )}

      {/* Mod seçimi: tek kelime (fikir havuzlu) | toplu analiz (Semrush Bulk Analysis) */}
      <div className="flex items-center gap-1 rounded-xl bg-gray-100 p-1 dark:bg-white/[0.06] w-fit">
        {([["single", "Tek Kelime Araştırması"], ["bulk", "Toplu Analiz (100'e kadar)"], ["gap", "Keyword Gap (Rakip Boşluğu)"]] as const).map(([k, label]) => (
          <button key={k} type="button" onClick={() => setInputMode(k)}
            className={`rounded-lg px-3.5 py-1.5 text-sm font-medium transition ${inputMode === k ? "bg-white text-gray-800 shadow-sm dark:bg-gray-800 dark:text-white" : "text-gray-500 hover:text-gray-700 dark:text-gray-400"}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Araştırma başlatma çubuğu */}
      <div className="flex flex-wrap items-start gap-2">
        {inputMode === "single" ? (
          <div className="flex min-w-[300px] flex-1 items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 dark:border-gray-800 dark:bg-white/[0.03]">
            <KeyRound className="h-4 w-4 shrink-0 text-gray-400" />
            <input
              value={seed}
              onChange={(e) => setSeed(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && void startResearch()}
              placeholder='Araştırılacak kelime (örn. "böcek ilaçlama")'
              className="w-full bg-transparent text-sm text-gray-700 outline-none placeholder:text-gray-400 dark:text-gray-200"
            />
          </div>
        ) : inputMode === "gap" ? (
          <div className="min-w-[300px] flex-1">
            <textarea
              value={compText}
              onChange={(e) => setCompText(e.target.value)}
              rows={3}
              placeholder={"Rakip alan adları (her satıra bir tane, en fazla 3):\nrakipsite.com\ndigersite.com.tr"}
              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-700 outline-none placeholder:text-gray-400 dark:border-gray-800 dark:bg-white/[0.03] dark:text-gray-200"
            />
            <div className={`mt-1 text-xs ${compCount > 3 ? "text-red-500" : "text-gray-400"}`}>
              Aktif siteniz otomatik dahil · {compCount} rakip{compCount > 3 ? " — ilk 3 analiz edilir" : ""}
            </div>
          </div>
        ) : (
          <div className="min-w-[300px] flex-1">
            <textarea
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              rows={5}
              placeholder={"Her satıra bir kelime (virgülle de ayırabilirsiniz) — en fazla 100:\nböcek ilaçlama\nev ilaçlama fiyatları\npire ilaçlama"}
              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-700 outline-none placeholder:text-gray-400 dark:border-gray-800 dark:bg-white/[0.03] dark:text-gray-200"
            />
            <div className={`mt-1 text-xs ${bulkCount > 100 ? "text-red-500" : "text-gray-400"}`}>
              {bulkCount} kelime{bulkCount > 100 ? " — ilk 100 analiz edilir" : ""}
            </div>
          </div>
        )}
        <select value={market} onChange={(e) => setMarket(e.target.value)}
          className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-700 outline-none dark:border-gray-800 dark:bg-white/[0.03] dark:text-gray-200">
          {MARKETS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
        </select>
        <button type="button" onClick={() => void startResearch()}
          disabled={running || (inputMode === "single" ? seed.trim().length < 2 : inputMode === "bulk" ? bulkCount < 2 : compCount < 1)}
          className="flex items-center gap-2 rounded-xl bg-brand-500 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-brand-600 disabled:opacity-50">
          <Search className="h-4 w-4" /> {running ? "Araştırılıyor…" : inputMode === "bulk" ? "Toplu Analiz Et" : inputMode === "gap" ? "Boşluğu Analiz Et" : "Araştır"}
        </button>
        {(list?.researches.length ?? 0) > 0 && (
          <div className="relative">
            <select
              value={activeId ?? ""}
              onChange={(e) => { setSelectedId(Number(e.target.value)); setGroup(null); setChecked(new Set()); }}
              className="appearance-none rounded-xl border border-gray-200 bg-white py-2.5 pl-3 pr-8 text-sm text-gray-700 outline-none dark:border-gray-800 dark:bg-white/[0.03] dark:text-gray-200"
            >
              {list!.researches.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.seed} · {r.market.toUpperCase()} {r.status === "running" ? "· sürüyor…" : r.status === "error" ? "· HATA" : `· ${r.ideaCount} fikir`}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2.5 top-3 h-4 w-4 text-gray-400" />
          </div>
        )}
      </div>

      {flash && <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-2.5 text-sm text-sky-800 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-300">{flash}</div>}

      {research?.status === "running" && (
        <div className="flex items-center gap-3 rounded-2xl border border-brand-200 bg-brand-50/60 px-4 py-3 text-sm text-brand-600 dark:border-brand-500/30 dark:bg-brand-500/10 dark:text-brand-300">
          <Search className="h-4 w-4 animate-pulse" /> "{research.seed}" araştırılıyor — hacim, zorluk, amaç ve kelime fikirleri çekiliyor (~15 sn)…
        </div>
      )}
      {research?.status === "done" && research.data && (
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 rounded-2xl border border-gray-200 bg-white px-4 py-3 dark:border-gray-800 dark:bg-white/[0.02]">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Araştırma</span>
          <span className="text-lg font-bold">{research.seed}</span>
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold uppercase dark:bg-white/[0.06]">{research.market}</span>
          <span className="text-[12px] text-gray-400">{new Date(research.createdAt).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })} · {research.data.ideas.length} kelime fikri{research.data.overview?.globalVolume ? ` · global hacim ${research.data.overview.globalVolume.toLocaleString("tr-TR")}` : ""}</span>
          <span className="ml-auto text-[11px] text-gray-400">geçmiş araştırmalar için üstteki listeyi kullan ↑</span>
        </div>
      )}

      {research?.status === "done" && research.data?.serp && (() => { const sp = research.data.serp!; const bizim = (u: string) => { try { const h = new URL(u).hostname.replace(/^www\./, ""); const d = (domains ?? []).find((x) => x.id === activeDomainId); const bizH = d ? d.url.replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0] : ""; return !!bizH && (h === bizH || h.endsWith("." + bizH)); } catch { return false; } }; return (
        <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-white/[0.02]">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold"><Search className="h-4 w-4 text-brand-500" /> SERP Analizi <span className="text-[11px] font-normal text-gray-400">— "{research.seed}" için gerçek Google sonuçları (araştırma anında)</span></div>
          {sp.aiOverview && (
            <div className="mb-3 rounded-xl border border-purple-200 bg-purple-50/60 px-3 py-2 dark:border-purple-900 dark:bg-purple-950/30">
              <div className="mb-0.5 text-[11px] font-bold uppercase tracking-wide text-purple-600 dark:text-purple-400">✦ AI Overview var</div>
              <p className="text-[12px] leading-relaxed text-gray-600 dark:text-gray-300">{sp.aiOverview.answer.slice(0, 220)}…</p>
              {sp.aiOverview.sources.length > 0 && <div className="mt-1 text-[11px] text-gray-400">Kaynaklar: {sp.aiOverview.sources.map((x) => x.domain).join(", ")}{sp.aiOverview.sources.some((x) => bizim(x.url)) ? " — BİZ VARIZ ✓" : " — biz yokuz"}</div>}
            </div>
          )}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="lg:col-span-2">
              {sp.organic.slice(0, 10).map((r) => (
                <div key={r.position} className={`flex items-start gap-2.5 border-b border-gray-50 py-1.5 text-[12.5px] dark:border-gray-800/60 ${bizim(r.url) ? "rounded bg-emerald-50/60 px-1.5 dark:bg-emerald-950/30" : ""}`}>
                  <span className="mt-0.5 w-5 shrink-0 text-right tabular-nums font-semibold text-gray-400">{r.position}</span>
                  <div className="min-w-0">
                    <a href={r.url} target="_blank" rel="noopener noreferrer" className="block truncate font-medium text-brand-600 hover:underline" title={r.url}>{r.title || r.url}</a>
                    <span className="text-[11px] text-gray-400">{r.domain}{bizim(r.url) && <b className="ml-1.5 text-emerald-600">BİZ ✓</b>}</span>
                  </div>
                </div>
              ))}
              {sp.organic.length === 0 && <p className="text-[12px] text-gray-400">SERP alınamadı.</p>}
            </div>
            <div>
              {sp.paa.length > 0 && (<>
                <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-gray-400">İlişkili Sorular (PAA)</div>
                {sp.paa.map((q) => <div key={q} className="border-b border-gray-50 py-1 text-[12px] dark:border-gray-800/60">{q}</div>)}
              </>)}
            </div>
          </div>
        </div>
      ); })()}
      {research?.status === "done" && research.data && !research.data.serp && (
        <p className="text-[11.5px] text-gray-400">Bu eski koşuda SERP analizi yok — yeni bir araştırma yaptığında "SERP Analizi" bölümü otomatik gelir (ilk 10 sonuç + AI Overview + PAA).</p>
      )}

      {research?.status === "error" && (
        <div className="flex items-center gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          <CircleAlert className="h-4 w-4" /> Araştırma başarısız: {research.error}
        </div>
      )}

      {/* GENEL BAKIŞ */}
      {ov && research?.status === "done" && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-white/[0.03]">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Aylık Hacim ({research.market.toUpperCase()})</div>
            <div className="mt-1.5 text-2xl font-semibold text-gray-800 dark:text-white">{fmtNum(ov.volume)}</div>
            {ov.globalVolume > 0 && <div className="mt-0.5 text-xs text-gray-400">Global: {fmtNum(ov.globalVolume)}</div>}
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-white/[0.03]">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Anahtar Kelime Zorluğu</div>
            <div className={`mt-1.5 text-2xl font-semibold ${kd.color}`}>{ov.difficulty ?? "—"}{ov.difficulty !== null && <span className="text-sm font-normal">%</span>}</div>
            <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-white/10">
              <div className="h-full rounded-full" style={{ width: `${ov.difficulty ?? 0}%`, background: kd.bar }} />
            </div>
            <div className={`mt-1 text-xs ${kd.color}`}>{kd.label}</div>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-white/[0.03]">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Arama Amacı</div>
            {intent
              ? <span className={`mt-2 inline-block rounded-full px-3 py-1 text-sm font-medium ${intent.cls}`}>{intent.label}</span>
              : <div className="mt-1.5 text-2xl font-semibold text-gray-300">—</div>}
            <div className="mt-1.5 text-xs text-gray-400">CPC: {ov.cpc ? `$${ov.cpc.toFixed(2)}` : "—"} · Rekabet: {ov.competition ?? "—"}</div>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-white/[0.03]">
            <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400"><TrendingUp className="h-3.5 w-3.5" /> 12 Aylık Trend</div>
            <div className="mt-3"><Sparkline data={ov.monthly} width={150} height={36} /></div>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-white/[0.03]">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400">SERP Özellikleri</div>
            <div className="mt-2 flex flex-wrap gap-1">
              {ov.serpFeatures.length ? ov.serpFeatures.slice(0, 6).map((f) => (
                <span key={f} className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600 dark:bg-white/10 dark:text-gray-300">{SERP_TR[f] ?? f}</span>
              )) : <span className="text-xs text-gray-300">—</span>}
            </div>
          </div>
        </div>
      )}

      {/* KEYWORD GAP SONUÇLARI */}
      {research?.status === "done" && research.data?.gap && (() => {
        const gap = research.data.gap;
        const catRows = gapCat === "hepsi" ? gap.rows : gap.rows.filter((r) => r.category === gapCat);
        const shown = (q.trim() ? catRows.filter((r) => r.keyword.includes(q.toLocaleLowerCase("tr"))) : catRows).slice(0, 300);
        const counts = { eksik: 0, zayif: 0, guclu: 0, ortak: 0 } as Record<GapRow["category"], number>;
        for (const r of gap.rows) counts[r.category]++;
        return (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              {GAP_CATS.map((c) => (
                <button key={c.key} type="button" onClick={() => setGapCat(c.key)} title={c.hint}
                  className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition ${gapCat === c.key ? "bg-brand-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-white/10 dark:text-gray-300"}`}>
                  {c.label} {c.key === "hepsi" ? `(${gap.rows.length})` : `(${counts[c.key as GapRow["category"]]})`}
                </button>
              ))}
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Sonuçlarda ara…"
                className="ml-auto w-44 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm outline-none dark:border-gray-800 dark:bg-white/[0.03] dark:text-gray-200" />
            </div>
            {gapCat !== "hepsi" && <p className="text-xs text-gray-400">{GAP_CATS.find((c) => c.key === gapCat)?.hint}</p>}
            <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs text-gray-400 dark:border-gray-800">
                    <th className="w-8 px-3 py-3" />
                    <th className="px-3 py-3 font-medium">Anahtar Kelime</th>
                    <th className="px-3 py-3 font-medium">Amaç</th>
                    <th className="px-3 py-3 text-right font-medium">Hacim</th>
                    <th className="px-3 py-3 text-right font-medium">KD %</th>
                    {gap.domains.map((d, i) => (
                      <th key={d} className={`px-3 py-3 text-center font-medium ${i === 0 ? "text-brand-500" : ""}`}>{i === 0 ? `${d} (siz)` : d}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r) => {
                    const im = r.intent ? INTENT_TR[r.intent] : null;
                    const rkd = kdMeta(r.difficulty);
                    return (
                      <tr key={r.keyword} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/60 dark:border-gray-800/60 dark:hover:bg-white/[0.03]">
                        <td className="px-3 py-2.5">
                          <input type="checkbox" checked={checked.has(r.keyword)} onChange={() => toggle(r.keyword)} className="h-4 w-4 accent-brand-500" />
                        </td>
                        <td className="px-3 py-2.5 font-medium text-gray-700 dark:text-gray-200">{r.keyword}</td>
                        <td className="px-3 py-2.5">
                          {im ? <span title={im.label} className={`inline-flex h-6 w-6 items-center justify-center rounded-md text-xs font-semibold ${im.cls}`}>{im.letter}</span> : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-700 dark:text-gray-200">{fmtNum(r.volume)}</td>
                        <td className={`px-3 py-2.5 text-right font-medium tabular-nums ${rkd.color}`}>{r.difficulty ?? "—"}</td>
                        {r.positions.map((p, i) => (
                          <td key={i} className="px-3 py-2.5 text-center"><PosBadge pos={p} /></td>
                        ))}
                      </tr>
                    );
                  })}
                  {!shown.length && (
                    <tr><td colSpan={5 + gap.domains.length} className="px-4 py-10 text-center text-gray-400">Bu kategoride kelime yok.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {checked.size > 0 && (
              <div className="sticky bottom-4 flex items-center justify-between rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-lg dark:border-gray-700 dark:bg-gray-900">
                <span className="text-sm text-gray-600 dark:text-gray-300"><b>{checked.size}</b> kelime seçildi</span>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => setChecked(new Set())} className="rounded-lg px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-100 dark:hover:bg-white/10">Temizle</button>
                  <button type="button" onClick={() => void addToTracking()}
                    className="flex items-center gap-1.5 rounded-lg bg-brand-500 px-3.5 py-1.5 text-sm font-medium text-white hover:bg-brand-600">
                    <Target className="h-4 w-4" /> Sıralama Takibine Ekle
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* FİKİRLER */}
      {research?.status === "done" && !research.data?.gap && ideas.length > 0 && (
        <div className="flex flex-col gap-4 lg:flex-row">
          {/* Grup paneli */}
          {groups.length > 0 && (
            <div className="w-full shrink-0 rounded-2xl border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-white/[0.03] lg:w-56">
              <div className="px-2 pb-2 text-xs font-semibold text-gray-500 dark:text-gray-400">GRUPLAR</div>
              <button type="button" onClick={() => setGroup(null)}
                className={`flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-sm ${!group ? "bg-brand-50 font-medium text-brand-600 dark:bg-brand-500/10 dark:text-brand-300" : "text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-white/[0.04]"}`}>
                <span>Tümü</span><span className="text-xs text-gray-400">{ideas.length}</span>
              </button>
              {groups.map(([g, n]) => (
                <button key={g} type="button" onClick={() => setGroup(group === g ? null : g)}
                  className={`flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-sm ${group === g ? "bg-brand-50 font-medium text-brand-600 dark:bg-brand-500/10 dark:text-brand-300" : "text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-white/[0.04]"}`}>
                  <span className="truncate">{g}</span><span className="ml-2 shrink-0 text-xs text-gray-400">{n}</span>
                </button>
              ))}
            </div>
          )}

          {/* Tablo */}
          <div className="min-w-0 flex-1 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              {([["tumu", `Tümü (${ideas.length})`], ["soru", `Sorular (${ideas.filter((i) => i.question).length})`], ["ilgili", `İlgili (${ideas.filter((i) => i.kind === "ilgili").length})`]] as const)
                .filter(([k]) => k !== "ilgili" || ideas.some((i) => i.kind === "ilgili")).map(([k, label]) => (
                <button key={k} type="button" onClick={() => setTab(k)}
                  className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition ${tab === k ? "bg-brand-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-white/10 dark:text-gray-300"}`}>
                  {label}
                </button>
              ))}
              <div className="ml-auto flex items-center gap-2">
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Sonuçlarda ara…"
                  className="w-44 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm outline-none dark:border-gray-800 dark:bg-white/[0.03] dark:text-gray-200" />
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                  className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm outline-none dark:border-gray-800 dark:bg-white/[0.03] dark:text-gray-200">
                  <option value="volume">Hacme göre</option>
                  <option value="difficulty">Zorluğa göre</option>
                  <option value="cpc">CPC'ye göre</option>
                </select>
              </div>
            </div>

            <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs text-gray-400 dark:border-gray-800">
                    <th className="w-8 px-3 py-3" />
                    <th className="px-3 py-3 font-medium">Anahtar Kelime</th>
                    <th className="px-3 py-3 font-medium">Amaç</th>
                    <th className="px-3 py-3 text-right font-medium">Hacim</th>
                    <th className="px-3 py-3 font-medium">Trend</th>
                    <th className="px-3 py-3 text-right font-medium" title="Anahtar Kelime Zorluğu">KD %</th>
                    <th className="px-3 py-3 text-right font-medium">CPC</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.slice(0, 200).map((i) => {
                    const im = i.intent ? INTENT_TR[i.intent] : null;
                    const ikd = kdMeta(i.difficulty);
                    return (
                      <tr key={i.keyword} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/60 dark:border-gray-800/60 dark:hover:bg-white/[0.03]">
                        <td className="px-3 py-2.5">
                          <input type="checkbox" checked={checked.has(i.keyword)} onChange={() => toggle(i.keyword)} className="h-4 w-4 accent-brand-500" />
                        </td>
                        <td className="px-3 py-2.5 font-medium text-gray-700 dark:text-gray-200">
                          {i.keyword}
                          {i.question && <span className="ml-2 rounded bg-sky-50 px-1.5 py-0.5 text-[10px] text-sky-600 dark:bg-sky-500/15 dark:text-sky-300">soru</span>}
                        </td>
                        <td className="px-3 py-2.5">
                          {im ? <span title={im.label} className={`inline-flex h-6 w-6 items-center justify-center rounded-md text-xs font-semibold ${im.cls}`}>{im.letter}</span> : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-700 dark:text-gray-200">{fmtNum(i.volume)}</td>
                        <td className="px-3 py-2.5"><Sparkline data={i.monthly} /></td>
                        <td className={`px-3 py-2.5 text-right font-medium tabular-nums ${ikd.color}`}>{i.difficulty ?? "—"}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-500 dark:text-gray-400">{i.cpc ? `$${i.cpc.toFixed(2)}` : "—"}</td>
                      </tr>
                    );
                  })}
                  {!filtered.length && (
                    <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">Bu filtreyle eşleşen kelime yok.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {checked.size > 0 && (
              <div className="sticky bottom-4 flex items-center justify-between rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-lg dark:border-gray-700 dark:bg-gray-900">
                <span className="text-sm text-gray-600 dark:text-gray-300"><b>{checked.size}</b> kelime seçildi</span>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => setChecked(new Set())} className="rounded-lg px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-100 dark:hover:bg-white/10">Temizle</button>
                  <button type="button" onClick={() => void addToTracking()}
                    className="flex items-center gap-1.5 rounded-lg bg-brand-500 px-3.5 py-1.5 text-sm font-medium text-white hover:bg-brand-600">
                    <Target className="h-4 w-4" /> Sıralama Takibine Ekle
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Boş durum + geçmiş yönetimi */}
      {!research && (
        <div className="rounded-2xl border border-gray-200 bg-white px-6 py-16 text-center text-gray-400 dark:border-gray-800 dark:bg-white/[0.03]">
          <KeyRound className="mx-auto mb-3 h-10 w-10 opacity-40" />
          <p className="text-sm">Bir kelime yazıp <b>Araştır</b>'a basın — hacim, zorluk, arama amacı, 12 aylık trend ve yüzlerce
          kelime fikri tek ekranda toplanır. Beğendiklerinizi seçip tek tıkla Sıralama Takibine gönderebilirsiniz.</p>
        </div>
      )}
      {(list?.researches.length ?? 0) > 0 && research && (
        <div className="flex items-center justify-between text-xs text-gray-400">
          <span>{new Date(research.createdAt).toLocaleString("tr-TR")} tarihli araştırma görüntüleniyor — tekrar görüntüleme ücretsizdir.</span>
          <button type="button" onClick={() => void removeResearch(list!.researches.find((r) => r.id === research.id)! )}
            className="flex items-center gap-1 rounded-lg px-2 py-1 text-gray-400 transition hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-500/10">
            <Trash2 className="h-3.5 w-3.5" /> Bu araştırmayı sil
          </button>
        </div>
      )}
    </div>
  );
}
